import { useState, useRef, useCallback } from "react";
import { IoClose } from "react-icons/io5";

import { FormHandles } from "@unform/core";
import { Form } from "@unform/web";
import * as Yup from "yup";
import { v4 } from "uuid";

import apiBackoffice from "../../../../../services/apiBackoffice";
import { useAuth } from "../../../../../hooks/auth";
import getValidationErrors from "../../../../../utils/getValidationErrors";

import Input from "../../../../../components/Atoms/Input";
import Loading from "../../../../../components/Atoms/Loading";
import Button from "../../../../../components/Atoms/Button";

import { SectionsModels } from "../../../../../models/SectionsModels";

import * as S from "./styles";

interface AddSectionTypes {
  onClose: () => void;
  refreshFeth: () => void;
}

const initialInputValues: SectionsModels = {
  schoolid: "",
  sectionid: "",
  title: "",
  type: "",
  position: "",
};

const AddSection: React.FC<AddSectionTypes> = ({ onClose, refreshFeth }) => {
  const [loading, setIsLoading] = useState(false);
  const [inputValues, setInputValues] = useState(initialInputValues);
  const { user } = useAuth();
  const formRef = useRef<FormHandles>(null);

  const handleInputChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setInputValues({
      ...inputValues,
      [name]: value,
    });
  };

  const handleSubmit = useCallback(
    async (data: SectionsModels) => {
      if (loading) return;

      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          title: Yup.string()
            .trim()
            .required("Título obrigatório")
            .min(3, "Título deve ter no mínimo 3 caracteres"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
          window.alert(err.errors.join(". "));
        }
        return;
      }

      setIsLoading(true);
      try {
        const generatedId = v4();

        const saveBody = {
          userid: user.userid,
          sectionid: generatedId,
          title: data.title.trim(),
          position: Number(data.position) || 1,
          schoolid: user.schoolid,
          type: "horizontal",
          levelid: "all",
          roomid: "all",
          profileid: "all",
        };

        const response = await apiBackoffice.post<any>(
          "backoffice/section/add",
          saveBody
        );

        // A Lambda responde vazio quando recusa o corpo: só "OK" confirma a gravação.
        if (response.data !== "OK") {
          throw new Error(
            response.data?.errorMessage ||
              response.data?.message ||
              "O servidor não confirmou a gravação. A seção não foi salva."
          );
        }

        window.alert("Seção Salva!");
        setInputValues(initialInputValues);
        onClose();
        refreshFeth();
      } catch (err: any) {
        window.alert(
          `Erro ao salvar a seção: ${
            err?.response?.data?.message ||
            err?.message ||
            "tente novamente."
          }`
        );
      } finally {
        setIsLoading(false);
      }
    },
    [user, user?.userid, loading]
  );

  return (
    <S.Container>
      <S.CloseModalButton onClick={onClose}>
        <IoClose color="grey" size={32} />
      </S.CloseModalButton>
      <S.HeaderContainer>
        <S.Title> Nova Seção </S.Title>
      </S.HeaderContainer>

      <S.Row>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          className="form big"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
          autoComplete="off"
        >
          <Input
            name="title"
            placeholder="Título"
            value={inputValues.title}
            onChange={(e) => handleInputChanges(e)}
          />
          <Input
            name="position"
            placeholder="Posição"
            value={inputValues.position}
            onChange={(e) => handleInputChanges(e)}
          />
          <div style={{ paddingBottom: "60px" }}>
            <Button type="submit" disabled={loading}>
              {loading ? <Loading size={2} /> : "Salvar"}
            </Button>
          </div>
        </Form>
      </S.Row>
    </S.Container>
  );
};

export default AddSection;
